import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CheckCircleIcon,
  ClockCountdownIcon,
  XCircleIcon,
  HandCoinsIcon,
  CrownSimpleIcon,
} from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });
  const toastedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          // Fire the cashback toast once — only when we actually earned some.
          if (!toastedRef.current && Number(data.plus_cashback_cents || 0) > 0) {
            toastedRef.current = true;
            const dollars = (data.plus_cashback_cents / 100).toFixed(2);
            toast.success(`+$${dollars} Plus cashback landed`, {
              description: "Credits added to your balance. Redeem on your next checkout.",
              icon: "👑",
              duration: 6000,
            });
          }
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ status: "timeout", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setState({ status: "error" });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  const data = state.data || {};
  const cashback = Number(data.plus_cashback_cents || 0);
  const applied = Number(data.credits_applied_cents || 0);
  const taxCents = Number(data.tax_amount_cents || 0);
  const totalCents = Number(data.amount_total_cents || 0);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="payment-success-page">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-16">
        {state.status === "checking" && (
          <div className="text-center">
            <ClockCountdownIcon size={56} className="mx-auto text-[#1D78AB]" />
            <h1 className="font-display font-black text-3xl mt-4">Confirming your payment…</h1>
            <p className="text-zinc-600 mt-2">This usually takes a few seconds.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div className="text-center">
            <CheckCircleIcon size={64} weight="fill" className="mx-auto text-emerald-500" />
            <h1 className="font-display font-black text-4xl mt-4">Payment successful</h1>
            <p className="text-zinc-600 mt-2">
              {totalCents > 0
                ? `You paid $${(totalCents / 100).toFixed(2)} (incl. $${(taxCents / 100).toFixed(2)} sales tax). The seller has been notified to ship your item.`
                : `You paid $${Number(data.amount).toFixed(2)}. The seller has been notified to ship your item.`}
            </p>

            {taxCents > 0 && (
              <div
                className="mt-6 mx-auto max-w-md rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-left"
                data-testid="tax-breakdown-card"
              >
                <p className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                  Receipt
                </p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600">Item subtotal</span>
                    <span className="font-mono text-zinc-900">
                      ${((totalCents - taxCents) / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600">Sales tax</span>
                    <span className="font-mono text-zinc-900">
                      ${(taxCents / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-zinc-200 pt-1.5 mt-1">
                    <span className="font-bold text-zinc-900">Total charged</span>
                    <span className="font-mono font-black text-zinc-900">
                      ${(totalCents / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {(cashback > 0 || applied > 0) && (
              <div
                className="mt-6 mx-auto max-w-md rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4 text-left"
                data-testid="plus-reward-card"
              >
                <div className="flex items-center gap-2">
                  <CrownSimpleIcon size={18} weight="fill" className="text-amber-500" />
                  <p className="text-xs font-bold tracking-widest uppercase text-amber-900">
                    Plus rewards
                  </p>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  {applied > 0 && (
                    <div className="flex items-center justify-between" data-testid="plus-reward-applied">
                      <span className="text-zinc-600">Credits redeemed</span>
                      <span className="font-black text-emerald-700 font-mono">
                        −${(applied / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {cashback > 0 && (
                    <div className="flex items-center justify-between" data-testid="plus-reward-cashback">
                      <span className="text-zinc-600 flex items-center gap-1.5">
                        <HandCoinsIcon size={14} className="text-amber-600" /> 1% cashback earned
                      </span>
                      <span className="font-black text-amber-700 font-mono">
                        +${(cashback / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/dashboard" data-testid="go-dashboard-btn">
                <Button className="bg-[#1D78AB] hover:bg-[#165d85] text-white">Go to dashboard</Button>
              </Link>
              <Link to="/browse">
                <Button variant="outline">Keep browsing</Button>
              </Link>
            </div>
          </div>
        )}
        {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
          <div className="text-center">
            <XCircleIcon size={56} className="mx-auto text-red-500" />
            <h1 className="font-display font-black text-3xl mt-4">Payment not completed</h1>
            <p className="text-zinc-600 mt-2">
              Your session may have expired. Please try again.
            </p>
            <Link to="/browse" className="inline-block mt-6">
              <Button variant="outline">Back to marketplace</Button>
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
